/**
 * Restrições de paragens explicitamente pedidas no texto, modeladas como
 * categorias/tipos/cozinhas e não como nomes de locais.
 *
 * Exemplo: "um restaurante japonês e um kartódromo em Leça da Palmeira" gera
 * duas restrições — restaurante com cozinha japonesa e atividade de kartódromo.
 * O Google Places é consultado com os tipos oficiais (japanese_restaurant,
 * go_karting_venue) e a query/nome serve apenas como sinal secundário.
 */

export type ConstraintKind = "restaurant" | "activity" | "cafe" | "bakery" | "shop" | "museum" | "park" | "viewpoint" | "hotel";

/**
 * Origem da restrição:
 *  - "tourism_family": derivada de uma família turística explícita da
 *    taxonomia (ex.: "só museus e miradouros"). A satisfação é decidida
 *    exclusivamente pelo predicado da taxonomia — não corre o ramo de
 *    pesquisa separada nem os tipos legados.
 *  - "named_place" | "food" | "text": restrições legadas, mantêm o caminho
 *    atual (tipos oficiais do Places + sinal secundário de nome).
 */
export type ConstraintSource = "tourism_family" | "named_place" | "food" | "text";

export type RequiredStopConstraint = {
  kind: ConstraintKind;
  /** Cozinha reconhecida (ex.: "japanese"). */
  cuisine?: string;
  /** Tipo de atividade interno (ex.: "go_karting"). */
  activityType?: string;
  /** Texto original do utilizador para esta restrição. */
  query: string;
  /** Origem da restrição. */
  source: ConstraintSource;
  /** Família turística (só quando source === "tourism_family"). */
  tourismFamily?: import("./tourism-taxonomy").TourismFamilyId;
};

/**
 * Mapeamento kind → família turística, usado para marcar restrições derivadas
 * de famílias explícitas. Só estas deixam de correr o ramo de pesquisa
 * separada; restrições de local nomeado, comida ou atividade continuam
 * vinculativas pelo caminho legado.
 */
const KIND_TO_TOURISM_FAMILY: Partial<Record<ConstraintKind, import("./tourism-taxonomy").TourismFamilyId>> = {
  museum: "museums_culture",
  viewpoint: "viewpoints_panoramas",
  park: "nature_parks",
};

/**
 * Marca como `tourism_family` as restrições cuja família foi explicitamente
 * pedida no pedido. Restrições que não mapeiam para nenhuma família explícita
 * mantêm `source: "text"` e o comportamento legado intacto.
 */
export function tagTourismFamilyConstraints(
  constraints: RequiredStopConstraint[],
  explicitFamilies: import("./tourism-taxonomy").TourismFamilyId[],
): RequiredStopConstraint[] {
  if (!explicitFamilies.length || !constraints.length) return constraints;
  return constraints.map((constraint) => {
    if (constraint.source !== "text") return constraint;
    const family = KIND_TO_TOURISM_FAMILY[constraint.kind];
    if (!family || !explicitFamilies.includes(family)) return constraint;
    return { ...constraint, source: "tourism_family" as const, tourismFamily: family };
  });
}

const CUISINE_PATTERNS: Array<{ id: string; re: RegExp }> = [
  { id: "japanese", re: /\b(?:japon[eê]s|japanese|japonesa|japones|sushi)\b/i },
  { id: "italian", re: /\b(?:italian[ao]?|italiano|italiana|pizza)\b/i },
  { id: "chinese", re: /\b(?:chin[eê]s|chinese|chinesa)\b/i },
  { id: "indian", re: /\b(?:indian[ao]?|indiano|indiana|indiano)\b/i },
  { id: "mexican", re: /\b(?:mexican[ao]?|mexicano|mexicana)\b/i },
  { id: "thai", re: /\b(?:thai|tailand[eê]s|tailandesa)\b/i },
  { id: "french", re: /\b(?:franc[eê]s|francesa|french)\b/i },
  { id: "greek", re: /\b(?:greg[ao]|greek|grega)\b/i },
  { id: "spanish", re: /\b(?:espanhol[ae]?|espanhola|spanish)\b/i },
  { id: "portuguese", re: /\b(?:portugu[eê]s|portuguesa|portuguese)\b/i },
  { id: "american", re: /\b(?:american[ao]?|americano|americana|burger|hamburger)\b/i },
  { id: "korean", re: /\b(?:corean[ao]?|coreano|coreana|korean)\b/i },
  { id: "vietnamese", re: /\b(?:vietnamit[ae]?|vietnamita|vietnamese)\b/i },
  { id: "brazilian", re: /\b(?:brasileir[ao]?|brasileira|brazilian)\b/i },
  { id: "seafood", re: /\b(?:mariscos?|peixe|seafood|fish)\b/i },
  { id: "steak", re: /\b(?:grelhados?|steakhouse|steak|churrasco)\b/i },
  { id: "vegetarian", re: /\b(?:vegetarian[ao]?|vegetariano|vegetariana)\b/i },
  { id: "vegan", re: /\b(?:vegan[ao]?|vegano|vegana)\b/i },
];

const ACTIVITY_PATTERNS: Array<{ id: string; kind: ConstraintKind; officialTypes: string[]; re: RegExp }> = [
  { id: "go_karting", kind: "activity", officialTypes: ["go_karting_venue"], re: /\b(?:kart[oó]dromos?|karting|go[-\s]?kart|kart)\b/i },
  { id: "bowling", kind: "activity", officialTypes: ["bowling_alley"], re: /\b(?:bowling|pista\s+de\s+bowling)\b/i },
  { id: "escape_room", kind: "activity", officialTypes: ["amusement_center"], re: /\b(?:escape\s+room|jogo\s+de\s+fuga)\b/i },
  { id: "aquarium", kind: "activity", officialTypes: ["aquarium"], re: /\b(?:aqu[áa]rio|aquarium)\b/i },
  { id: "zoo", kind: "activity", officialTypes: ["zoo"], re: /\b(?:jardim\s+zool[oó]gico|zoo|zool[oó]gico)\b/i },
  { id: "cinema", kind: "activity", officialTypes: ["movie_theater"], re: /\b(?:cinema|cine|cinemas?)\b/i },
  { id: "theater", kind: "activity", officialTypes: ["performing_arts_theater"], re: /\b(?:teatro|theater|theatre)\b/i },
  { id: "casino", kind: "activity", officialTypes: ["casino"], re: /\b(?:casino|casinos?)\b/i },
];

const CATEGORY_PATTERNS: Array<{ kind: ConstraintKind; officialTypes: string[]; re: RegExp }> = [
  { kind: "restaurant", officialTypes: ["restaurant", "meal_takeaway", "meal_delivery"], re: /\b(?:restaurantes?|restaurants?|tascas?|bistr[oô]s?|onde\s+comer|sítio\s+para\s+comer|lugar\s+para\s+comer)\b/i },
  { kind: "cafe", officialTypes: ["cafe", "coffee_shop"], re: /\b(?:caf[eé]s?|coffee\s+shops?|cafeter[ií]as?)\b/i },
  { kind: "bakery", officialTypes: ["bakery"], re: /\b(?:pastelarias?|confeitarias?|padarias?|baker(?:y|ies))\b/i },
  { kind: "museum", officialTypes: ["museum"], re: /\b(?:museus?|museums?)\b/i },
  { kind: "park", officialTypes: ["park", "national_park"], re: /\b(?:parques?|jardins?\s+p[uú]blicos?|parks?)\b/i },
  { kind: "viewpoint", officialTypes: ["viewpoint"], re: /\b(?:miradour\w+|viewpoints?)\b/i },
  { kind: "shop", officialTypes: ["shopping_mall", "department_store", "store"], re: /\b(?:lojas?|shopping|centros?\s+comerciais|shops?)\b/i },
  { kind: "hotel", officialTypes: ["lodging"], re: /\b(?:hotel|hostel|pousada|alojamento|onde\s+dormir|onde\s+ficar)\b/i },
];

export function cuisineForConstraint(constraint: RequiredStopConstraint): string | undefined {
  return constraint.cuisine;
}

export function officialTypesForConstraint(constraint: RequiredStopConstraint): string[] {
  if (constraint.kind === "restaurant" && constraint.cuisine) {
    return [`${constraint.cuisine}_restaurant`];
  }
  if (constraint.activityType) {
    const activity = ACTIVITY_PATTERNS.find((a) => a.id === constraint.activityType);
    if (activity) return activity.officialTypes;
  }
  const category = CATEGORY_PATTERNS.find((c) => c.kind === constraint.kind);
  return category?.officialTypes ?? [];
}

export function placesSearchQuery(constraint: RequiredStopConstraint): string {
  if (constraint.kind === "restaurant" && constraint.cuisine) {
    return `${constraint.cuisine} restaurant`;
  }
  if (constraint.activityType === "go_karting") return "go kart";
  return constraint.query;
}

/**
 * Aliases de pesquisa por restrição, em ordem de prioridade. A primeira é a
 * query principal; as seguintes só são usadas enquanto faltarem candidatos
 * (controlo de custo — ver place-availability.server.ts).
 */
const ACTIVITY_SEARCH_ALIASES: Record<string, string[]> = {
  go_karting: ["kartódromo", "karting", "go-kart", "kart track"],
  bowling: ["bowling", "bowling alley"],
  escape_room: ["escape room"],
  aquarium: ["aquário", "aquarium"],
  zoo: ["zoo", "jardim zoológico"],
  cinema: ["cinema", "movie theater"],
  theater: ["teatro", "theatre"],
  casino: ["casino"],
};

const CUISINE_SEARCH_ALIASES: Record<string, string[]> = {
  japanese: ["japanese restaurant", "restaurante japonês", "sushi"],
  italian: ["italian restaurant", "restaurante italiano", "pizzeria"],
  chinese: ["chinese restaurant", "restaurante chinês"],
  indian: ["indian restaurant", "restaurante indiano"],
  thai: ["thai restaurant", "restaurante tailandês"],
  korean: ["korean restaurant", "restaurante coreano"],
  vietnamese: ["vietnamese restaurant"],
  mexican: ["mexican restaurant", "restaurante mexicano"],
  seafood: ["seafood restaurant", "marisqueira"],
  steak: ["steakhouse", "churrasqueira"],
  vegetarian: ["vegetarian restaurant", "restaurante vegetariano"],
  vegan: ["vegan restaurant", "restaurante vegano"],
};

export function searchAliasesForConstraint(constraint: RequiredStopConstraint): string[] {
  const primary = placesSearchQuery(constraint);
  const extra =
    (constraint.activityType ? ACTIVITY_SEARCH_ALIASES[constraint.activityType] : undefined) ??
    (constraint.cuisine ? CUISINE_SEARCH_ALIASES[constraint.cuisine] : undefined) ??
    [];
  const seen = new Set<string>();
  return [primary, ...extra, constraint.query]
    .map((value) => value.trim())
    .filter((value) => {
      if (!value) return false;
      const key = normalizeToken(value);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 4);
}


function normalizeToken(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function hasOfficialType(place: { primaryType?: string | null; types?: string[] }, types: string[]): boolean {
  if (!types.length) return false;
  const primary = place.primaryType ? normalizeToken(place.primaryType) : "";
  if (types.some((t) => normalizeToken(primary).includes(normalizeToken(t)))) return true;
  const placeTypes = (place.types ?? []).map((t) => normalizeToken(t));
  return types.some((t) => placeTypes.includes(normalizeToken(t)));
}

export function constraintMatchesPlace(
  constraint: RequiredStopConstraint,
  place: { primaryType?: string | null; types?: string[]; displayName?: { text?: string } },
): boolean {
  const officialTypes = officialTypesForConstraint(constraint);
  if (officialTypes.length && hasOfficialType(place, officialTypes)) return true;
  // Sinal secundário: o nome contém a query original (sem ser substituição).
  const name = place.displayName?.text ?? "";
  const queryTokens = normalizeToken(constraint.query).split(/\s+/).filter((t) => t.length >= 3);
  if (queryTokens.length && queryTokens.some((t) => normalizeToken(name).includes(t))) return true;
  return false;
}

const QUANTITY_PREFIX = /\b(?:um|uma|uns|umas|an?|the|o|a|os|as|alguns?|algumas?)\s+/gi;

function cleanQuery(value: string): string {
  return value
    .replace(QUANTITY_PREFIX, "")
    .replace(/\b(?:bom|boa|bons|boas|bem\s+avaliad[oa]s?|t[ií]pic[oa]s?|local|locais|good|best|well[- ]rated|typical)\b/gi, "")
    .replace(/\s+(?:perto|pr[oó]xim[oa]|junto|na\s+rota|no\s+percurso|near|close\s+to|along\s+the\s+route)\b.*$/i, "")
    .replace(/\s+/g, " ")
    .trim();
}

function originalMatch(prompt: string, re: RegExp): string | undefined {
  const m = prompt.match(re);
  return m ? m[0] : undefined;
}

/**
 * Extrai restrições de paragens explicitamente pedidas no texto, modeladas como
 * categorias/tipos/cozinhas e não como nomes de locais.
 */
export function extractRequiredStopConstraints(prompt: string): RequiredStopConstraint[] {
  const normalized = prompt.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const constraints: RequiredStopConstraint[] = [];
  const seenKinds = new Set<string>();

  const add = (constraint: RequiredStopConstraint) => {
    const baseKey = `${constraint.kind}:${constraint.cuisine ?? ""}:${constraint.activityType ?? ""}`;
    // Restaurante genérico não duplica um restaurante com cozinha.
    if (constraint.kind === "restaurant" && !constraint.cuisine && seenKinds.has(`restaurant:${constraint.cuisine ?? ""}:`)) return;
    if (seenKinds.has(baseKey)) return;
    seenKinds.add(baseKey);
    constraints.push(constraint);
  };

  // Restaurantes com cozinha explícita: "restaurante japonês", "jantar italiano"
  const restaurantPattern = /\b(?:restaurantes?|jantar|almo[cç]ar|comer)\s+([\p{L}\p{M}'’-]{2,40}(?:\s+(?:de|do|da|dos|das|del|di|du|of|the|[\p{L}\p{M}'’-]{2,40})){0,2})/giu;
  let match: RegExpExecArray | null;
  while ((match = restaurantPattern.exec(normalized)) !== null) {
    const tail = match[1];
    const cuisine = CUISINE_PATTERNS.find(({ re }) => re.test(tail));
    if (cuisine) {
      const original = originalMatch(prompt, /\brestaurantes?\s+japon[eê]s\w*/i) ?? `restaurante ${tail}`;
      add({ kind: "restaurant", cuisine: cuisine.id, query: cleanQuery(original), source: "text" });
    }
  }

  // Atividades específicas
  for (const activity of ACTIVITY_PATTERNS) {
    const m = prompt.match(activity.re);
    if (m) {
      add({ kind: activity.kind, activityType: activity.id, query: cleanQuery(m[0]), source: "text" });
    }
  }

  // Outras categorias (apenas se não houver uma restrição mais específica já)
  for (const category of CATEGORY_PATTERNS) {
    if (category.kind === "restaurant") continue; // já tratado acima
    const m = prompt.match(category.re);
    if (m) {
      add({ kind: category.kind, query: cleanQuery(m[0]), source: "text" });
    }
  }

  return constraints.slice(0, 6);
}
